import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ShieldCheck, Eye, HandHeart, Sparkles, Building2, Briefcase, HeartHandshake, ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SectionHeader from "@/components/SectionHeader";
import { fetchImpactStats, fetchProjects, fetchTiers } from "@/lib/api";

const TRUST = [
  { icon: HandHeart, title: "100% to builds", desc: "Every pound funds a real website" },
  { icon: Building2, title: "Real businesses", desc: "UK-based startups and small traders" },
  { icon: Eye, title: "Transparent", desc: "Public process, public outcomes" },
  { icon: ShieldCheck, title: "Built by pros", desc: "Same team behind paid £449–£2,499 builds" },
];

const STEPS = [
  { n: "01", title: "Donations or sponsors fund builds", desc: "Individuals and businesses contribute toward a website pool — from £50 to £250+." },
  { n: "02", title: "Applicants apply and get reviewed", desc: "We review applications and prioritise those with the strongest case for impact." },
  { n: "03", title: "We design and build the website", desc: "A founder-led team builds a production-ready site — usually in 2–3 weeks." },
  { n: "04", title: "Business launches online", desc: "We hand over the site, domain, and full ownership. The business keeps everything." },
];

const ELIGIBILITY = [
  { icon: Sparkles, title: "Startups with low budget", desc: "Pre-revenue founders with a clear idea but no cash for an agency build." },
  { icon: HeartHandshake, title: "People on benefits or just starting out", desc: "Anyone using self-employment to build a way out — we want to back you." },
  { icon: Briefcase, title: "Small local businesses", desc: "Independent shops, makers, and tradespeople who need a real online presence." },
];

const PARTNERS = ["Local Council", "Citizens Advice", "Prince's Trust", "Enterprise Nation", "UnLtd", "Federation of Small Businesses"];

export default function Landing() {
  const [stats, setStats] = useState({ websites_built: 0, businesses_helped: 0, funds_raised_gbp: 0, sponsors_count: 0 });
  const [projects, setProjects] = useState([]);
  const [tiers, setTiers] = useState({});

  useEffect(() => {
    fetchImpactStats().then(setStats).catch(() => {});
    fetchProjects().then(setProjects).catch(() => {});
    fetchTiers().then(setTiers).catch(() => {});
  }, []);

  const tierEntries = Object.entries(tiers);
  const hasProjects = projects.length > 0;
  const hasStats = (stats.websites_built ?? 0) > 0 || (stats.businesses_helped ?? 0) > 0;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* HERO — light, bold typography matching dhwebsiteservices.co.uk */}
      <section className="relative bg-white pt-28 md:pt-32 pb-24 md:pb-28 overflow-hidden">
        {/* Subtle dotted backdrop */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.5]"
          style={{
            backgroundImage:
              "radial-gradient(rgba(15,23,42,0.07) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
            maskImage:
              "radial-gradient(ellipse at center, black 35%, transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 35%, transparent 75%)",
          }}
        />
        {/* Soft blue ambient glow */}
        <div
          aria-hidden
          className="absolute top-1/2 right-0 w-[60vw] h-[60vw] -translate-y-1/2 translate-x-1/3 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 60%)" }}
        />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 px-4 py-2 mb-8 animate-fade-up" data-testid="hero-eyebrow">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-[13.5px] font-medium text-slate-700">A free websites initiative by DH Website Services</span>
          </div>

          <h1
            className="font-display font-semibold tracking-[-0.04em] text-balance text-slate-900 text-[44px] sm:text-6xl md:text-7xl lg:text-[104px] leading-[0.95] max-w-5xl mx-auto animate-fade-up delay-1"
            data-testid="hero-title"
          >
            Help small businesses,
            <br />
            <span className="text-blue-600">get online for free.</span>
          </h1>

          <p
            className="mt-9 mx-auto text-lg md:text-xl text-slate-500 max-w-2xl leading-relaxed animate-fade-up delay-2"
            data-testid="hero-subtitle"
          >
            Production-ready websites for people who can&apos;t afford one. Funded by donations and sponsors, built by professionals, owned by the founder.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center animate-fade-up delay-3">
            <Link
              to="/apply"
              data-testid="hero-apply-btn"
              className="inline-flex items-center gap-2 justify-center rounded-full bg-blue-600 text-white font-semibold px-7 py-4 hover:bg-blue-700 shadow-[0_10px_30px_-10px_rgba(37,99,235,0.5)] hover:shadow-[0_15px_40px_-10px_rgba(37,99,235,0.65)] transition-all hover:-translate-y-0.5"
            >
              Apply for a free website <ArrowRight size={18} />
            </Link>
            <Link
              to="/donate"
              data-testid="hero-sponsor-btn"
              className="inline-flex items-center gap-2 justify-center rounded-full bg-white border border-slate-300 text-slate-900 font-semibold px-7 py-4 hover:bg-slate-50 hover:border-slate-400 transition-all"
            >
              Sponsor a website
            </Link>
          </div>

          <p className="mt-7 text-[13px] text-slate-500 flex flex-wrap justify-center items-center gap-x-3 gap-y-1 animate-fade-up delay-4" data-testid="hero-trust-text">
            <span>Run by DH Website Services</span>
            <span className="text-slate-300">•</span>
            <span>Transparent process</span>
            <span className="text-slate-300">•</span>
            <span>Funded by sponsors</span>
          </p>
        </div>

        {/* Hero stats row — flat horizontal style like reference */}
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 mt-20 md:mt-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 md:gap-x-12">
            <HeroStat label="Founder-led" sub="Direct accountability" />
            <HeroStat label="100%" sub="Funds to builds" />
            <HeroStat label="Free" sub="For approved applicants" />
            <HeroStat label="2–3 weeks" sub="Typical build time" />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {TRUST.map((item) => (
              <div
                key={item.title}
                className="flex flex-col gap-2.5"
                data-testid={`trust-item-${item.title.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "")}`}
              >
                <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <item.icon size={18} strokeWidth={1.8} />
                </div>
                <p className="font-display text-[16px] font-semibold text-slate-900 tracking-tight">{item.title}</p>
                <p className="text-[13.5px] text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-slate-50 py-24 md:py-32 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="The process"
            title="Simple, transparent,"
            accentWord="end-to-end."
            accentSerif
            description="Four steps from sponsor to launch. No hidden hands. No hidden fees."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                data-testid={`step-${i + 1}`}
                className="relative bg-white border border-slate-100 rounded-3xl p-8 transition-all duration-300 hover:shadow-[0_24px_60px_-20px_rgba(15,23,42,0.15)] hover:-translate-y-1"
              >
                <span className="font-display text-blue-600 text-[13px] font-semibold tracking-wider">{s.n}</span>
                <p className="mt-5 font-display text-[20px] font-semibold text-slate-900 tracking-tight leading-snug">{s.title}</p>
                <p className="mt-3 text-[14.5px] text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO CAN APPLY */}
      <section id="apply" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <SectionHeader
              eyebrow="Who can apply"
              title="Built for the people"
              accentWord="agencies skip."
              accentSerif
              description="If you&apos;ve been told a website is too expensive, you&apos;re exactly who we&apos;re here for."
            />
            <div className="mt-8">
              <Link to="/apply" data-testid="who-apply-btn" className="btn-primary">
                Apply now <ArrowRight size={18} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col divide-y divide-slate-100 border-y border-slate-100">
            {ELIGIBILITY.map((e) => (
              <div key={e.title} className="py-8 grid grid-cols-12 gap-6 items-start" data-testid={`eligibility-${e.title.toLowerCase().split(" ")[0]}`}>
                <div className="col-span-2 md:col-span-1 pt-1">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                    <e.icon size={18} strokeWidth={1.8} />
                  </div>
                </div>
                <div className="col-span-10 md:col-span-11">
                  <p className="font-display text-[20px] md:text-[22px] font-semibold text-slate-900 tracking-tight leading-snug">{e.title}</p>
                  <p className="mt-2 text-[15px] text-slate-600 leading-relaxed max-w-xl">{e.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPONSOR / DONATE */}
      <section id="sponsors" className="bg-slate-50 py-24 md:py-32 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Fund a build"
            title="Fund a business."
            accentWord="Change someone's start."
            accentSerif
            description="Pick a tier or set your own amount. Every donation goes directly into the next build."
            align="center"
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {tierEntries.length === 0 && [1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-3xl border border-slate-100 h-[340px] animate-pulse" />
            ))}
            {tierEntries.map(([key, t], i) => {
              const featured = key === "builder";
              return (
                <div
                  key={key}
                  data-testid={`tier-${key}`}
                  className={`relative bg-white rounded-3xl p-8 border transition-all duration-300 hover:-translate-y-1 ${
                    featured ? "tier-highlight border-transparent" : "border-slate-100 hover:shadow-[0_24px_60px_-20px_rgba(15,23,42,0.12)]"
                  }`}
                >
                  {featured && (
                    <span className="absolute -top-3 left-8 bg-blue-600 text-white text-[11px] font-semibold tracking-[0.18em] uppercase px-3 py-1 rounded-full">
                      Most popular
                    </span>
                  )}
                  <p className="font-display text-[12px] font-semibold text-blue-600 uppercase tracking-[0.18em]">{t.label}</p>
                  <p className="mt-6 font-display text-5xl md:text-6xl font-semibold tracking-[-0.03em] text-slate-900">£{t.amount.toFixed(0)}</p>
                  <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{t.description}</p>
                  <ul className="mt-6 space-y-2.5">
                    {[
                      i === 0 ? "Partial funding for one build" : i === 1 ? "Covers domain + design phase" : "Sponsors a full website build",
                      "Listed in our public sponsors wall",
                      "100% of your gift goes into the next build",
                    ].map((l) => (
                      <li key={l} className="flex items-start gap-2 text-[14px] text-slate-600">
                        <Check size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{l}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/donate?tier=${key}`}
                    data-testid={`tier-${key}-btn`}
                    className={featured ? "btn-primary mt-8 w-full justify-center" : "btn-outline-light mt-8 w-full justify-center"}
                  >
                    Choose {t.label}
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/donate" data-testid="sponsors-donate-btn" className="btn-primary">
              Donate now
            </Link>
            <a href="mailto:foundation@dhwebsiteservices.co.uk?subject=Become a sponsor" data-testid="sponsors-become-btn" className="btn-outline-light">
              Become a sponsor
            </a>
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section id="impact" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Real impact"
            title="Real businesses."
            accentWord="Real outcomes."
            accentSerif
            description="A few of the founders we&apos;ve helped get online — fully sponsored, fully owned by them."
          />

          {hasProjects ? (
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
              {projects.map((p, idx) => (
                <article
                  key={p.id}
                  data-testid={`project-${idx}`}
                  className="group bg-slate-50 rounded-3xl overflow-hidden border border-slate-100 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_80px_-20px_rgba(15,23,42,0.18)]"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                    <img src={p.image_url} alt={p.business_name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-7 md:p-8">
                    <div className="flex items-center justify-between gap-4">
                      <p className="eyebrow text-blue-600">{p.category}</p>
                      {p.location && <span className="text-[12px] text-slate-500">{p.location}</span>}
                    </div>
                    <h3 className="mt-3 font-display text-2xl md:text-[28px] font-semibold text-slate-900 tracking-tight">{p.business_name}</h3>
                    <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{p.description}</p>
                    {p.live_url ? (
                      <a href={p.live_url} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-1.5 text-blue-600 text-[14px] font-medium hover:gap-2.5 transition-all">
                        View live site <ExternalLink size={14} />
                      </a>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-16 rounded-3xl border border-dashed border-slate-200 bg-slate-50/40 p-10 md:p-16 text-center">
              <p className="font-display text-[22px] md:text-[26px] font-semibold text-slate-900 tracking-tight">The first build is on its way.</p>
              <p className="mt-3 text-slate-500 max-w-xl mx-auto">As soon as we ship a site, it lands here — with the founder, the brief, and the live link.</p>
              <div className="mt-8 flex gap-3 justify-center">
                <Link to="/apply" className="btn-primary">Be the first applicant</Link>
                <Link to="/donate" className="btn-outline-light">Sponsor build #1</Link>
              </div>
            </div>
          )}

          {hasStats && (
            <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-slate-100 pt-12">
              <ImpactStat value={stats.websites_built} label="Websites built" />
              <ImpactStat value={stats.businesses_helped} label="Businesses helped" />
              <ImpactStat value={`£${(stats.funds_raised_gbp ?? 0).toLocaleString()}`} label="Funds raised" />
              <ImpactStat value={stats.sponsors_count} label="Sponsors" />
            </div>
          )}
        </div>
      </section>

      {/* PARTNERS */}
      <section className="bg-slate-50 py-24 md:py-28 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Partners"
            title="Partner with us to support"
            accentWord="more businesses."
            accentSerif
            description="We work with local councils, charities, and business support groups to find the founders who need help most."
            align="center"
          />
          <div className="mt-14 overflow-hidden">
            <div className="flex gap-12 animate-marquee whitespace-nowrap">
              {[...PARTNERS, ...PARTNERS].map((p, i) => (
                <div key={i} className="flex-shrink-0 px-8 py-5 rounded-2xl bg-white border border-slate-100 font-display text-slate-500 font-medium text-[18px] tracking-tight">
                  {p}
                </div>
              ))}
            </div>
          </div>
          <div className="mt-12 text-center">
            <a href="mailto:foundation@dhwebsiteservices.co.uk?subject=Partnership enquiry" data-testid="partners-cta-btn" className="btn-outline-light">
              Become a partner
            </a>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.04em] leading-[1.02] text-balance text-slate-900">
            Apply or support{" "}
            <span className="text-blue-600">today.</span>
          </h2>
          <p className="mt-6 text-slate-500 text-lg max-w-2xl mx-auto">
            Every application reviewed within 7 days. Every donation goes straight into the next build.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/apply" data-testid="final-apply-btn" className="btn-primary">
              Apply <ArrowRight size={18} />
            </Link>
            <Link to="/donate" data-testid="final-donate-btn" className="btn-outline-light">
              Donate
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function HeroStat({ label, sub }) {
  return (
    <div className="text-left">
      <p className="font-display text-2xl md:text-[28px] font-semibold text-slate-900 tracking-tight">{label}</p>
      <p className="mt-1.5 text-[13px] text-slate-500">{sub}</p>
    </div>
  );
}

function ImpactStat({ value, label }) {
  return (
    <div className="text-left">
      <p className="font-display text-4xl md:text-5xl font-semibold tracking-[-0.03em] text-slate-900">{value}</p>
      <p className="mt-2 text-[12px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
    </div>
  );
}
